import { useState, useMemo, useEffect } from 'react';
import { Plus, Trash2, Clock, ShieldCheck, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SlideOver, SlideOverFooter } from '@/components/Shared/SlideOver';
import { Button } from '@/components/Shared/Button';
import { FormField } from '@/components/Shared/FormField';
import { Badge } from '@/components/Shared/Badge';
import {
  useTimeclockStore,
  type DriverRole,
  type TimeClockEventKey,
  type WorkLocation,
} from '@/store/useTimeclockStore';
import { useAuthStore } from '@/store/useAuthStore';
import { useDescribeVehicle } from '@/hooks/useDescribeVehicle';
import { parseAxiosError, translateApiError } from '@/errors';
import {
  FacilityFlowCoreModulesPlanningEntitiesTimeClockEventType as TimeClockEventTypeEnum,
  FacilityFlowCoreModulesPlanningEntitiesTimeClockDayStatus as TimeClockDayStatusEnum,
} from '@/api/generated/models';
import { isHomeOffice, stretchLocationsByTime } from '@/utils/workLocation';
import { WorkLocationSelect } from './WorkLocationSelect';
import type { FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockDayDto } from '@/api/generated/models';
import {
  formatUtcToLocalTime,
  utcToLocalTimeInputValue,
  localTimeToUtcIso,
  parseUtcTimestamp,
} from '@/utils/dateUtils';

const EVENT_TYPE_KEYS: Record<number, string> = {
  [TimeClockEventTypeEnum.NUMBER_1]: 'edit.eventTypes.clockIn',
  [TimeClockEventTypeEnum.NUMBER_2]: 'edit.eventTypes.clockOut',
  [TimeClockEventTypeEnum.NUMBER_3]: 'edit.eventTypes.breakStart',
  [TimeClockEventTypeEnum.NUMBER_4]: 'edit.eventTypes.breakEnd',
};

const EVENT_KEYS: Record<number, TimeClockEventKey> = {
  [TimeClockEventTypeEnum.NUMBER_1]: 'clockIn',
  [TimeClockEventTypeEnum.NUMBER_2]: 'clockOut',
  [TimeClockEventTypeEnum.NUMBER_3]: 'breakStart',
  [TimeClockEventTypeEnum.NUMBER_4]: 'breakEnd',
};

const EVENT_TYPE_VALUES = [
  TimeClockEventTypeEnum.NUMBER_1,
  TimeClockEventTypeEnum.NUMBER_2,
  TimeClockEventTypeEnum.NUMBER_3,
  TimeClockEventTypeEnum.NUMBER_4,
];


interface EditableEvent {
  id: string;
  eventType: number;
  timeValue: string;
  isNew: boolean;
  /** Vehicle and role of an old stamp (before ADR-0027) — shown only; trips are no longer stamped. */
  vehicleId: string | null;
  driverRole: DriverRole | null;
  /** Chosen at clock-in and stored there only. */
  workLocation: WorkLocation | null;
}

function isClockIn(eventType: number): boolean {
  return eventType === TimeClockEventTypeEnum.NUMBER_1;
}

interface TimeClockEditSlideOverProps {
  isOpen: boolean;
  onClose: () => void;
  entry: FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockDayDto | null;
  isAdmin: boolean;
  employeeId: string;
}

export default function TimeClockEditSlideOver({
  isOpen,
  onClose,
  entry,
  isAdmin,
  employeeId,
}: TimeClockEditSlideOverProps) {
  const { recordEvent, correctDay, selfCorrectDay, loading } = useTimeclockStore();
  const { t } = useTranslation('timeclock');
  const describeVehicle = useDescribeVehicle();
  // This view edits the signed-in user's own days: home office only with their own permission.
  const canChooseLocation = useAuthStore((s) => s.employee?.allowsHomeOffice ?? false);
  const isConfirmed = entry?.status === TimeClockDayStatusEnum.NUMBER_2;
  const canEditExisting = !isConfirmed || isAdmin;
  const canAddEvents = !isConfirmed || isAdmin;

  const initialEvents = useMemo<EditableEvent[]>(() => {
    if (!entry?.events) return [];
    return [...entry.events]
      .sort((a, b) => {
        const at = parseUtcTimestamp(a.timestamp)?.getTime() ?? 0;
        const bt = parseUtcTimestamp(b.timestamp)?.getTime() ?? 0;
        return at - bt;
      })
      .map((evt) => ({
        id: evt.timeClockEventId ?? crypto.randomUUID(),
        eventType: evt.eventType ?? TimeClockEventTypeEnum.NUMBER_1,
        timeValue: utcToLocalTimeInputValue(evt.timestamp),
        isNew: false,
        vehicleId: evt.vehicleId ?? null,
        driverRole: evt.driverRole ?? null,
        workLocation: evt.workLocation ?? null,
      }));
  }, [entry?.events]);

  const [events, setEvents] = useState<EditableEvent[]>(initialEvents);
  const [correctionNote, setCorrectionNote] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setEvents(initialEvents);
    setCorrectionNote('');
    setSaveError(null);
  }, [initialEvents]);

  const hasChanges = useMemo(() => {
    if (events.length !== initialEvents.length) return true;
    return events.some((evt, i) => {
      const orig = initialEvents[i];
      if (!orig) return true;
      return (
        evt.eventType !== orig.eventType ||
        evt.timeValue !== orig.timeValue ||
        evt.workLocation !== orig.workLocation
      );
    });
  }, [events, initialEvents]);

  const resetState = () => {
    setEvents(initialEvents);
    setCorrectionNote('');
    setSaveError(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleAddEvent = () => {
    setEvents((prev) => {
      const lastLocation = [...prev].reverse().find((e) => e.workLocation);
      return [
        ...prev,
        {
          id: crypto.randomUUID(),
          eventType: TimeClockEventTypeEnum.NUMBER_1,
          timeValue: '',
          isNew: true,
          vehicleId: null,
          driverRole: null,
          workLocation: canChooseLocation ? (lastLocation?.workLocation ?? null) : null,
        },
      ];
    });
  };

  const handleLocationChange = (id: string, change: Partial<Pick<EditableEvent, 'workLocation'>>) => {
    setEvents((prev) => prev.map((e) => (e.id === id ? { ...e, ...change } : e)));
  };

  const stretchLocations = useMemo(() => stretchLocationsByTime(events), [events]);
  const isHomeOfficeStretch = (evt: EditableEvent) => isHomeOffice(stretchLocations.get(evt));

  const handleRemoveEvent = (id: string) => {
    setEvents((prev) => prev.filter((e) => e.id !== id));
  };

  const handleEventTypeChange = (id: string, value: number) => {
    setEvents((prev) =>
      prev.map((e) => (e.id === id ? { ...e, eventType: value } : e)),
    );
  };

  const handleTimeChange = (id: string, value: string) => {
    setEvents((prev) =>
      prev.map((e) => (e.id === id ? { ...e, timeValue: value } : e)),
    );
  };

  const handleSave = async () => {
    if (!entry?.date) return;
    const dateStr = entry.date;
    setSaveError(null);

    const emptyTimeEvents = events.filter((e) => !e.timeValue);
    if (emptyTimeEvents.length > 0) {
      setSaveError(t('edit.missingTimes'));
      return;
    }

    const existingEventsChanged = events.some((evt, i) => {
      if (evt.isNew) return false;
      const orig = initialEvents[i];
      if (!orig) return true;
      return (
        evt.eventType !== orig.eventType ||
        evt.timeValue !== orig.timeValue ||
        evt.workLocation !== orig.workLocation
      );
    });
    const eventsRemoved = initialEvents.length > events.filter((e) => !e.isNew).length;

    const newEvents = events
      .filter((e) => e.isNew && e.timeValue)
      .map((e) => ({ ...e, timestamp: localTimeToUtcIso(dateStr, e.timeValue) }))
      .sort((a, b) => a.timestamp.localeCompare(b.timestamp));
    // why: the backend rejects a single event earlier than the day's last one, so anything
    // inserted before it has to go through the full correction of the whole day.
    const lastExistingMs = Math.max(
      0,
      ...(entry.events ?? []).map((e) => parseUtcTimestamp(e.timestamp)?.getTime() ?? 0),
    );
    const newEventsOutOfOrder = newEvents.some(
      (e) => new Date(e.timestamp).getTime() < lastExistingMs,
    );
    const needsFullCorrection = existingEventsChanged || eventsRemoved || newEventsOutOfOrder;

    const correctedEvents = events
      .filter((e) => e.timeValue)
      .map((e) => ({
        type: e.eventType as typeof TimeClockEventTypeEnum[keyof typeof TimeClockEventTypeEnum],
        timestamp: localTimeToUtcIso(dateStr, e.timeValue),
        // A kept stamp keeps its id; the logbook is not touched by a correction (ADR-0027).
        ...(e.isNew ? {} : { timeClockEventId: e.id }),
        // Only clock-ins carry the work location.
        ...(isClockIn(e.eventType) && e.workLocation ? { workLocation: e.workLocation } : {}),
      }))
      .sort((a, b) => a.timestamp.localeCompare(b.timestamp));

    try {
      if (isAdmin && (needsFullCorrection || isConfirmed)) {
        await correctDay(
          employeeId,
          dateStr,
          correctedEvents,
          correctionNote || t('edit.correctionDefault'),
        );
      } else if (needsFullCorrection && !isConfirmed) {
        await selfCorrectDay(dateStr, correctedEvents);
      } else {
        for (const evt of newEvents) {
          const key = EVENT_KEYS[evt.eventType];
          if (key) {
            await recordEvent(
              key,
              dateStr,
              evt.timestamp,
              undefined,
              isClockIn(evt.eventType) ? (evt.workLocation ?? undefined) : undefined,
            );
          }
        }
      }

      handleClose();
    } catch (err) {
      setSaveError(translateApiError(parseAxiosError(err)));
    }
  };

  const dateFormatted = entry?.date
    ? (() => {
        const [y, m, d] = entry.date.split('-');
        return `${d}.${m}.${y}`;
      })()
    : '';

  return (
    <SlideOver
      isOpen={isOpen}
      onClose={handleClose}
      title={`${t('edit.title')} – ${dateFormatted}`}
      size="lg"
      hasUnsavedChanges={hasChanges}
      footer={
        canEditExisting ? (
          <SlideOverFooter
            onCancel={handleClose}
            onConfirm={handleSave}
            confirmText={t('edit.save')}
            cancelText={t('edit.cancel')}
            loading={loading}
            confirmDisabled={!hasChanges && !events.some((e) => e.isNew)}
          />
        ) : undefined
      }
    >
      <div className="space-y-5">
        {saveError && (
          <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" strokeWidth={2} />
            <p className="text-[13px] text-red-700">{saveError}</p>
          </div>
        )}

        {isConfirmed && (
          <div className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 px-4 py-3">
            <ShieldCheck className="w-4 h-4 text-sky-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
            <div>
              <p className="text-[13px] font-medium text-sky-800">
                {t('edit.confirmedTitle')}
              </p>
              <p className="text-[12px] text-sky-700 mt-0.5">
                {isAdmin ? t('edit.confirmedAdmin') : t('edit.confirmedUser')}
              </p>
            </div>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[13px] font-semibold text-slate-900">
              {t('edit.events')}
            </h3>
            {canAddEvents && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleAddEvent}
                leftIcon={<Plus className="w-3.5 h-3.5" strokeWidth={2} />}
              >
                {t('edit.addEvent')}
              </Button>
            )}
          </div>

          {events.length === 0 ? (
            <div className="text-[13px] text-slate-500 py-4 text-center">
              {t('edit.noEvents')}
            </div>
          ) : (
            <div className="space-y-2">
              {events.map((evt) => {
                const isEditable = canEditExisting || evt.isNew;
                return (
                  <div
                    key={evt.id}
                    className="flex flex-wrap items-center gap-2 sm:gap-3 p-2.5 rounded-md border border-slate-200 bg-white"
                  >
                    <Clock className="w-4 h-4 text-slate-400 flex-shrink-0" strokeWidth={2} />

                    {isEditable ? (
                      <>
                        <select
                          value={evt.eventType}
                          onChange={(e) =>
                            handleEventTypeChange(evt.id, Number(e.target.value))
                          }
                          aria-label={t('edit.eventType')}
                          className="px-2 py-1 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50"
                        >
                          {EVENT_TYPE_VALUES.map((value) => (
                            <option key={value} value={value}>
                              {t(EVENT_TYPE_KEYS[value])}
                            </option>
                          ))}
                        </select>
                        <input
                          type="time"
                          value={evt.timeValue}
                          onChange={(e) => handleTimeChange(evt.id, e.target.value)}
                          className="w-28 px-2 py-1 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50"
                        />
                      </>
                    ) : (
                      <>
                        <Badge>
                          {t(EVENT_TYPE_KEYS[evt.eventType] ?? 'edit.eventTypes.clockIn')}
                        </Badge>
                        <span className="text-[13px] font-medium text-slate-900 tabular-nums">
                          {evt.timeValue || '--:--'}
                        </span>
                      </>
                    )}

                    <div className="flex-1" />

                    {evt.isNew && (
                      <Badge variant="info">
                        {t('edit.new')}
                      </Badge>
                    )}

                    {isEditable && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveEvent(evt.id)}
                        title={t('edit.remove')}
                        aria-label={t('edit.remove')}
                        className="p-1.5"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" strokeWidth={2} />
                      </Button>
                    )}

                    {isClockIn(evt.eventType) && isEditable && canChooseLocation ? (
                      <div className="basis-full pl-6">
                        <WorkLocationSelect
                          value={evt.workLocation}
                          onChange={(workLocation) => handleLocationChange(evt.id, { workLocation })}
                        />
                      </div>
                    ) : (
                      // Only home office is called out — office is the normal case.
                      isHomeOfficeStretch(evt) && (
                        <p className="basis-full pl-6 text-[12px] text-slate-500">
                          {t('workLocation.homeOffice')}
                        </p>
                      )
                    )}

                    {!evt.isNew && evt.driverRole && (
                      <p className="basis-full pl-6 text-[12px] text-slate-500">
                        {describeVehicle({ driverRole: evt.driverRole, vehicleId: evt.vehicleId })}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {canEditExisting && isConfirmed && isAdmin && (
          <FormField
            label={t('edit.correctionNoteLabel')}
            value={correctionNote}
            onChange={(e) => setCorrectionNote(e.target.value)}
            placeholder={t('edit.correctionNotePlaceholder')}
          />
        )}

        {entry?.correctionNote && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-[11px] font-medium text-amber-800 mb-1">
              {t('edit.lastCorrection')}
            </p>
            <p className="text-[13px] text-amber-800">
              {entry.correctionNote}
            </p>
            {entry.correctedAt && (
              <p className="text-[11px] text-amber-700 mt-1 tabular-nums">
                {formatUtcToLocalTime(entry.correctedAt)}
              </p>
            )}
          </div>
        )}
      </div>
    </SlideOver>
  );
}
